/* drawlz-size-bridge.js — makes the sitewide size actually reach the OLD
 * collection and product templates, and drives the reshape on the NEW one.
 *
 * THE PROBLEM. assets/drawlz-size.js stores the size and exposes
 * window.DrawlzSize, and sections/dzc-rail.liquid consumes it (subscribe ->
 * reshape). But the templates customers actually land on do not:
 *
 *   collections -> collection.og-2-0-collection-page / originalz-cp /
 *                  cottonz-cp / expressionz-cp render Dawn's
 *                  main-collection-product-grid. Its cards are plain
 *                  li.grid__item with none of the data-* attributes
 *                  reshape() needs.
 *   products    -> product.og-product-page / ct- / ex- render Dawn's
 *                  <variant-radios>, which has no size integration.
 *   collection.cp-master (the NEW grid) emits perfectly good cards and
 *                  then never calls reshape at all — see below.
 *
 * WHY THIS IS A SCRIPT AND NOT A TEMPLATE EDIT. sections/main-product.liquid
 * is 120,660 bytes and sections/drawlz-collection.liquid is 55,219 — both far
 * past any safe whole-file re-emit — and template_suffix must not be
 * reassigned because that resource field is shared with the LIVE theme. So the
 * bridge layers on top of the rendered DOM instead. Nothing here edits a
 * template, and deleting the one line that loads it in
 * snippets/content_for_footer.liquid reverts all of it.
 *
 * BEHAVIOUR MIRRORS THE RAILS, DELIBERATELY. reshape() sorts ready-in-size
 * first and marks the rest; it never hides. That is why this does NOT drive
 * Shopify's native size FACET, even though the collection pages have one: a
 * facet filters products out of the grid. Auto-applying it on load would hide
 * stock the shopper never asked to hide, fight the back button, and behave
 * differently from the homepage. The facet stays exactly as it is, shopper-
 * driven; this layer only sorts and labels.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     WHO PARTICIPATES

     An ALLOWLIST by product type, not a blocklist. Only the adult underwear
     line bands by size; anything else — existing or added later — renders
     exactly as it does today. A new product type defaults to NOT
     participating, which is the safe direction: not participating just means
     normal rendering, never a hidden or dead-ended product.

     Type alone is not sufficient in one direction. The catalog's types are:

       Boxer Brief      originalz, cottonz, expressionz  -> participates
       Boxer Brief      limited-capsules                 -> EXCLUDED (owner)
       Boys' underwear  kidz                             -> excluded by type
       sock             socks                            -> excluded by type
       tshirt / T-Shirt graphic-tees                     -> excluded by type
       "" (empty)       suedeskin crew                   -> excluded by type

     Limited capsules share the Boxer Brief type with the core line, so type
     cannot separate them. Every product carries collection:<handle> tags, so
     the capsules are excluded on the tag instead of on a hardcoded handle
     list that would go stale the moment a new capsule drops.

     NOTE ON isUnderwearScale(). It cannot do this job: the tee
     420-cosmic-indo-t-shirt is S-4XL and kidz is S-XL, both of which sit
     entirely on the underwear ladder and would pass. It stays as a secondary
     guard, not the gate.
     --------------------------------------------------------------------- */
  var PARTICIPATING_TYPE = 'boxer brief';
  var EXCLUDED_TAGS = [
    'collection:limited-capsules',
    'collection:socks',
    'collection:kidz',
    'collection:graphic-tees'
  ];
  var EXCLUDED_PRODUCTS = ['icon-sock', 'suedeskin-crew-premium-mens-crew-neck-t-shirt'];

  /* Whole-page fast exit. Redundant with the per-product rule above — every
     product in these collections already fails it — but it states the owner's
     requirement literally and independently of catalog tagging, and saves the
     feed request on pages that can never reshape. */
  var EXCLUDED_COLLECTIONS = ['socks', 'graphic-tees', 'limited-capsules', 'kidz'];

  /* Accepts either shape: /collections/x/products.json uses product_type,
     /products/x.js uses type. Both return tags as an array. */
  function participates(p) {
    if (!p) return false;
    if (EXCLUDED_PRODUCTS.indexOf(p.handle) !== -1) return false;
    var type = String(p.product_type || p.type || '').trim().toLowerCase();
    if (type !== PARTICIPATING_TYPE) return false;
    var tags = (p.tags || []).map(function (t) { return String(t).trim().toLowerCase(); });
    for (var i = 0; i < EXCLUDED_TAGS.length; i++) {
      if (tags.indexOf(EXCLUDED_TAGS[i]) !== -1) return false;
    }
    return true;
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* The hint's own styling, for the OLD grid only. snippets/dzc-card-css.liquid
     carries .dzc-hint for the new card system, but that snippet is not rendered
     by the old collection template, so a bridged hint would otherwise be
     unstyled. Kept here rather than in a stylesheet so the whole bridge stays
     one file. Colours match the card system: green ready, brand red sold-out. */
  function injectStyle() {
    if (document.getElementById('dzc-hint-bridged-css')) return;
    var s = document.createElement('style');
    s.id = 'dzc-hint-bridged-css';
    s.textContent =
      '.dzc-hint--bridged{font-size:11.5px;font-weight:600;letter-spacing:.02em;' +
      'margin-top:5px;min-height:1em;line-height:1.35}' +
      '.is-ready-size .dzc-hint--bridged{color:#1f7a3d}' +
      '.is-oos-size .dzc-hint--bridged{color:#c8161d}';
    document.head.appendChild(s);
  }
  function handleFromPath(kind) {
    var m = location.pathname.match(new RegExp('/' + kind + '/([^/?#]+)'));
    return m ? m[1] : null;
  }
  /* ---------------------------------------------------------------------
     FEED FETCH WITH BACKOFF

     Storefront JSON endpoints throttle per-burst, and this script runs at
     DOMContentLoaded — the busiest moment on the page. A 429 there is not a
     failure to give up on, it is "come back in a moment", so treat those
     statuses as retryable and everything else as final. Retry-After wins
     when present. Silent on final failure, by design: the caller's job is
     enhancement, and no feed simply means the page behaves as it does today.
     --------------------------------------------------------------------- */
  var RETRY_STATUS = [429, 430, 500, 502, 503, 504];
  /* Four attempts, not three (2026-08-10). The three-attempt version was
     observed LOSING in the wild: on /collections/originalz every one of its
     attempts came back 429 and the grid was left completely untagged, while
     the identical URL fetched by hand moments later returned 200 with all 14
     products three times running. The burst simply outlasts a 6.5s window.
     Widened to ~14.5s, and paired with the deferral of the empty-cart
     recommendation fetch in drawlz-cart.js, which was a second avoidable
     caller of the same endpoint on every single page load.
     See SORT_CUTOFF_MS below for why a late success must not re-sort. */
  var RETRY_BACKOFF_MS = [700, 1800, 4000, 8000];

  /* A late feed must LABEL but never REORDER. reshape() moves cards in the DOM
     to put ready-in-size first; doing that several seconds in would shuffle the
     grid under a shopper who has already started reading it, which is worse
     than not sorting at all. reshape() already takes {sort:false} for exactly
     this. A size the shopper changes themselves always sorts, whenever it
     happens — that one is a deliberate action, not a background arrival. */
  var SORT_CUTOFF_MS = 2500;

  /* How late a retried PDP feed may still pre-select a size. The retry ladder
     runs to ~14.5s; changing someone's size that far into reading the page is
     startling and can quietly change what they buy. Two attempts' worth of
     benefit, then drop it. */
  var PDP_FEED_CUTOFF_MS = 8000;

  function fetchFeed(url, done) {
    var attempt = 0;
    function again() {
      if (attempt >= RETRY_BACKOFF_MS.length) return false;
      setTimeout(go, RETRY_BACKOFF_MS[attempt++]);
      return true;
    }
    function go() {
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (r.ok) return r.json();
          if (RETRY_STATUS.indexOf(r.status) !== -1) {
            /* Retry-After is seconds (or an HTTP date, which we ignore). */
            var ra = parseFloat(r.headers.get('Retry-After'));
            if (ra > 0 && ra <= 10 && attempt < RETRY_BACKOFF_MS.length) {
              attempt++;
              setTimeout(go, ra * 1000);
            } else {
              again();
            }
          }
          return null;
        })
        .then(function (json) {
          if (!json) return;
          /* The caller's own work must NOT re-enter the retry path. Without
             this guard a throw from done() would land in .catch below and be
             treated as a failed request, re-running the whole tagging pass on
             a grid it had already half-tagged. */
          try { done(json); } catch (e) { /* silent, as before */ }
        })
        .catch(function () { again(); });   /* network blip: same treatment */
    }
    go();
  }

  /* WHERE THE BRIDGED HINT GOES.

     Dawn's card renders .card__information TWICE — once inside .card__inner
     (hidden by component-card.css on media cards) and once in the outer
     .card__content, which is the live one holding the title and price. The
     old code asked for '.card__information, .card-information, .card__content'
     and querySelector returns the first match in DOCUMENT ORDER across the
     whole list, not the first match of the first selector — that is the
     .card__content INSIDE .card__inner, i.e. the image area. The hint was
     therefore parented into the media box rather than under the title.

     This is structural on purpose and must not become a computed-style test:
     component-card.css is inside the theme's first-interaction lazy loader, so
     at the moment this runs the "hidden" copy is not actually hidden yet and
     any visibility check would pick the wrong one. `.card > .card__content`
     cannot match the inner copy, whose parent chain goes through .card__inner.

     Landing here also makes the hint a direct child of the two-column grid
     added in section 5 of assets/drawlz-sitewide.css, so it takes
     grid-column:1/-1 and auto-places on its own row beneath the title/price
     row — the intended position, with no CSS of its own. */
  function hintHost(card) {
    return card.querySelector('.card > .card__content > .card__information')
        || card.querySelector('.card__content > .card__information')
        || card.querySelector('.card-information')
        || card.querySelector('.card__content')
        || card;
  }

  function sizeIndexOf(options) {
    for (var i = 0; i < options.length; i++) {
      var n = String(options[i] && options[i].name ? options[i].name : options[i]).toLowerCase();
      if (n.indexOf('size') !== -1) return i;
    }
    return 0;
  }

  /* =====================================================================
     COLLECTION PAGES

     Two grids, one rule. Which one is on the page decides how much work
     this does:

     NEW GRID (.dzc-grid, from sections/drawlz-collection.liquid, template
     collection.cp-master). It already emits [data-dzc-card] with
     data-handle, a [data-dzc-hint] slot, and — verified against
     products.json — an AVAILABILITY-AWARE data-sizes (the anniversary LE
     renders data-sizes="S,M,L,2XL", its in-stock set, not all seven
     variants). So nothing here re-tags it. Two things are still missing:

       1. it never calls reshape, so the stored size did nothing at all; and
       2. its data-size-axis does NOT encode the participation rule.
          Graphic tees come through as axis="multi" with S-4XL and kidz as
          axis="sized" with S-XL. reshape() only skips axis="none", so both
          would band by size — exactly the products the owner excluded.

     So on the new grid this stamps data-size-scale="other" on everything
     that fails participates(), then drives the reshape. The stamp is
     ADDITIVE ONLY — a card the section already opted out stays opted out.

     OLD GRID (Dawn's li.grid__item). Nothing is tagged, so this tags it all
     from the feed and injects the hint element as well.
     ===================================================================== */
  function bridgeCollection() {
    /* THERE IS MORE THAN ONE GRID. cp-master splits the collection into a
       singles grid and a SETS grid (.dzc-setsdivide), and reshaping only the
       first left all eight set cards unbanded. Every .dzc-grid is reshaped,
       each against itself — sorting must stay inside its own grid so sets
       never sort up into the singles block. */
    var grids = Array.prototype.slice.call(document.querySelectorAll('.dzc-grid'))
      .filter(function (g) { return g.querySelector('[data-dzc-card]'); });
    var isNew = grids.length > 0;
    if (!isNew) {
      var oldGrid = document.querySelector('#product-grid, .collection .grid, ul.product-grid');
      if (!oldGrid) return;
      /* the old-grid path must not run over an already-tagged grid */
      if (document.querySelector('[data-dzc-card]')) return;
      grids = [oldGrid];
    }

    var collection = handleFromPath('collections');
    if (!collection) return;
    if (EXCLUDED_COLLECTIONS.indexOf(collection) !== -1) return;

    var cards = [];
    grids.forEach(function (g) {
      cards = cards.concat(Array.prototype.slice.call(
        isNew ? g.querySelectorAll('[data-dzc-card]')
              : g.querySelectorAll('li.grid__item, .grid__item')));
    });
    if (!cards.length) return;
    if (!isNew) injectStyle();

    /* ONE request for the whole page. Per-card /products/{handle}.js would be
       50+ requests on a big collection and gets rate-limited (429) fast. It
       also carries product_type and tags, so participates() costs nothing
       extra here.

       AND IT MUST RETRY — this is why the hint never appeared on a real
       collection page. One request is not the same as one request that
       succeeds: at DOMContentLoaded this page is already mid-burst (the
       hc-sale-labels app sprays /products/*.js, and something else asks for
       the same feed at ?limit=12), so Shopify answered THIS request with
       429. The old code was `r.ok ? r.json() : null` followed by
       `if (!json) return`, i.e. a 429 was indistinguishable from "nothing to
       do" and failed silently: no card tagged, no hint element created, no
       console trace. Verified in the network log — the load-time request
       returned 429 while the identical URL fetched a moment later returned
       200 with all 14 products.

       So: back off and try again on the statuses that mean "too fast, not
       broken", honouring Retry-After when the server sends one. Still fails
       silently after the last attempt — the grid then renders exactly as it
       does today, which is the whole point of this being a bridge. */
    var feedStartedAt = Date.now();
    fetchFeed('/collections/' + encodeURIComponent(collection) + '/products.json?limit=250',
      function (json) {
        if (!json || !json.products) return;

        var byHandle = {};
        json.products.forEach(function (p) {
          var si = sizeIndexOf(p.options || []);
          var key = 'option' + (si + 1);
          var sizes = [];
          (p.variants || []).forEach(function (v) {
            if (!v.available) return;
            var s = v[key];
            if (s && sizes.indexOf(s) === -1) sizes.push(s);
          });
          byHandle[p.handle] = { sizes: sizes, ok: participates(p) };
        });

        cards.forEach(function (card) {
          var handle, rec;

          if (isNew) {
            handle = card.getAttribute('data-handle');
            rec = handle ? byHandle[handle] : null;
            /* FAIL CLOSED. A card we cannot confirm from the feed is opted
               out rather than guessed at — never band something unverified.
               Additive: a card the section already opted out is untouched. */
            if (!rec || !rec.ok) card.setAttribute('data-size-scale', 'other');
            return;
          }

          var link = card.querySelector('a[href*="/products/"]');
          if (!link) return;
          var hm = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
          if (!hm) return;
          handle = hm[1];
          rec = byHandle[handle];
          if (!rec) return;                   /* not in the feed — leave untouched */

          card.setAttribute('data-dzc-card', '');
          card.setAttribute('data-handle', handle);
          card.setAttribute('data-sizes', rec.sizes.join(','));
          card.setAttribute('data-size-axis', 'size');

          /* the explicit opt-out hook reshape() already honours. Non-
             participating products keep their card and their place in the
             middle group — labelled with nothing, hidden from nothing. */
          if (!rec.ok) card.setAttribute('data-size-scale', 'other');

          if (!card.querySelector('[data-dzc-hint]')) {
            var hint = document.createElement('div');
            hint.setAttribute('data-dzc-hint', '');
            hint.className = 'dzc-hint dzc-hint--bridged';
            hintHost(card).appendChild(hint);
          }
        });

        if (!window.DrawlzSize) return;
        function reshapeAll(opts) {
          grids.forEach(function (g) { window.DrawlzSize.reshape(g, opts); });
        }
        /* First application only: suppress the re-order if the feed took so
           long that the shopper is probably already reading the grid. */
        reshapeAll((Date.now() - feedStartedAt) > SORT_CUTOFF_MS ? { sort: false } : undefined);
        /* WRAPPED, not passed by reference. subscribe() emits the SIZE to its
           callbacks, so handing it reshapeAll directly would arrive as `opts`
           and be read as an options object. Shopper-driven changes always sort. */
        window.DrawlzSize.subscribe(function () { reshapeAll(); });
      });
  }

  /* =====================================================================
     PRODUCT PAGES — pre-select the stored size on Dawn's <variant-radios>.

     THE TIMING TRAP THIS SOLVES. This theme blocks ~20 scripts behind a
     first-interaction loader (the script[data-src] / yett pair at the top of
     snippets/content_for_footer.liquid). global.min.js is one of them, so at
     DOMContentLoaded <variant-radios> is an un-upgraded HTMLElement with no
     change listener at all. Ticking a radio and dispatching 'change' then
     does nothing: the radio flips, the theme never hears it, and the form's
     hidden input[name="id"] keeps the ORIGINAL variant. The page would read
     "4XL" while Add to Cart still added S.

     So we wait for the custom element to be defined, apply, and then VERIFY
     that the form actually moved. If it did not, the radio is put back. The
     PDP either changes as a whole — radio, hidden input, price, availability
     — or it does not change at all. It never misreports what is in the cart.

     Never blocks and never hides. A sold-out size is still selected so the
     shopper lands on the normal sold-out / notify state for the size they
     actually wear, which is the same thing the PDP does when they pick it by
     hand.
     ===================================================================== */
  function sizeRadios(host) {
    return Array.prototype.slice.call(host.querySelectorAll('input[type="radio"]'))
      .filter(function (r) { return String(r.name).toLowerCase().indexOf('size') !== -1; });
  }

  /* initialValue is the VALUE of the size that was checked when the page
     settled — deliberately a string, not the element. See below. */
  function applySize(host, size, initialValue) {
    var radios = sizeRadios(host);
    if (!radios.length) return;

    /* Abort if anything moved the selection while we were waiting — the
       shopper's own first click is what loads the theme's scripts in the
       first place, so this is a real race, and their choice always wins.

       COMPARE BY VALUE, NOT BY NODE (2026-08-11). This used to hold the
       original radio ELEMENT and test `current !== initial`. The product
       section re-renders during load (price / inventory / availability
       refresh), which swaps the radios for fresh nodes — after which the
       held reference is detached and that check can never pass again, so the
       stored size silently never applied. Values survive a re-render; node
       identity does not. */
    var currentEl = radios.filter(function (r) { return r.checked; })[0] || null;
    var currentValue = currentEl ? currentEl.value : null;
    if (currentValue !== initialValue) return;

    /* secondary guard; the type allowlist is the real gate */
    var values = radios.map(function (r) { return r.value; });
    if (!window.DrawlzSize.isUnderwearScale(values)) return;

    var target = radios.filter(function (r) { return r.value === size; })[0];
    if (!target || target.checked) return;

    /* THE MAIN PRODUCT FORM, explicitly. document.querySelector on
       form[action*="/cart/add"] returns whichever comes first in the document,
       and on this theme that is Shopify's INSTALLMENT form, not the buy form.
       Dawn happens to update both, so it worked by luck; ask for the right one. */
    var form = (host.dataset.section && document.getElementById('product-form-' + host.dataset.section))
               || document.querySelector('form[action*="/cart/add"]');
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    var before = idInput ? idInput.value : null;

    target.checked = true;
    /* Dawn's variant-radios listens for change and updates variant, URL,
       price and the add-to-cart state from it. */
    target.dispatchEvent(new Event('change', { bubbles: true }));

    /* updateVariantInput() is synchronous inside onVariantChange, so one
       macrotask is enough; the second check covers themes that defer it. */
    function settled() { return idInput && idInput.value !== before; }
    function revert() {
      if (settled()) return;
      /* re-query: the node we flipped may itself have been replaced by now */
      var live = sizeRadios(host);
      var back = live.filter(function (r) { return r.value === initialValue; })[0];
      var t = live.filter(function (r) { return r.value === size; })[0] || target;
      if (t) t.checked = false;
      if (back) back.checked = true;
    }
    setTimeout(function () { if (!settled()) setTimeout(revert, 400); }, 0);
  }

  /* Wait for <pickup-availability> to upgrade, when the page has one.
     If it never arrives we do NOT apply: firing anyway throws inside Dawn and
     leaves the shopper on a size they did not choose. Silently skipping is the
     documented safe failure for this whole bridge. */
  function whenPickupReady(cb) {
    if (!document.querySelector('pickup-availability')) { cb(); return; }
    if (!window.customElements) { cb(); return; }
    if (window.customElements.get('pickup-availability')) { cb(); return; }
    var settled = false;
    window.customElements.whenDefined('pickup-availability').then(function () {
      if (!settled) { settled = true; cb(); }
    });
    setTimeout(function () { settled = true; }, 5000);   /* give up quietly */
  }

  /* DO NOT APPLY UNTIL THE PAGE HAS SETTLED (2026-08-11).

     This is the fix for a race that cost real time, so the evidence is worth
     recording. Instrumenting the live file showed every guard passing and the
     change event being dispatched — and then:

         tick0 settled= false   now= <the ORIGINAL variant>
         REVERTING

     i.e. Dawn accepted the change and still did not move the cart form. The
     identical dispatch, run by hand a moment later on the same page, worked
     perfectly: the event reached <variant-radios>, currentVariant became 4XL,
     the form input updated and the URL gained ?variant=. Nothing is wrong with
     the logic; it simply cannot succeed at ~1.5s into the load, while the PDP's
     own scripts (drawlz-pdp.js, drawlz-native-pdp.js and the section's own
     wiring) are still initialising around it.

     Rather than keep fighting for a slightly earlier apply, wait for the page
     to finish. The size lands a beat later than before and does so reliably.
     Every guard still applies afterwards, so a shopper who has already chosen
     a size in the meantime is never overridden. */
  function whenSettled(cb) {
    function go() { setTimeout(cb, 400); }
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  /* Wait for <pickup-availability> to upgrade, when the page has one.
     Dawn's onVariantChange calls updatePickupAvailability() BEFORE
     updateVariantInput() and only checks the ELEMENT exists, never that it has
     upgraded — fire early and it throws, the variant never reaches the cart
     form, and this feature silently does nothing. variant-radios comes from
     global.min.js (promoted first) while pickup-availability.js is promoted
     second, so the two are ready at different moments. If it never arrives we
     do NOT apply: throwing would leave the shopper on a size they did not pick.
     Silently skipping is the documented safe failure for this whole bridge. */
  function whenPickupReady(cb) {
    if (!document.querySelector('pickup-availability')) { cb(); return; }
    if (!window.customElements) { cb(); return; }
    if (window.customElements.get('pickup-availability')) { cb(); return; }
    var settled = false;
    window.customElements.whenDefined('pickup-availability').then(function () {
      if (!settled) { settled = true; cb(); }
    });
    setTimeout(function () { settled = true; }, 5000);   /* give up quietly */
  }

  function bridgeProduct() {
    if (!window.DrawlzSize) return;
    var size = window.DrawlzSize.get();
    if (!size) return;

    var handle = handleFromPath('products');
    if (!handle) return;

    var host = document.querySelector('variant-radios, variant-selects');
    if (!host) return;
    /* the NEW PDP has its own pill row and its own wiring — do not double-drive it */
    if (document.querySelector('[data-drawlz-size]')) return;

    /* Snapshot the selection BEFORE the wait, so applySize can tell "still
       untouched" from "the shopper already picked something". Stored as a
       VALUE, not a node — the section re-renders during load and any element
       reference we keep here would be detached by the time we use it. */
    var initialEl = sizeRadios(host).filter(function (r) { return r.checked; })[0] || null;
    var initialValue = initialEl ? initialEl.value : null;

    /* One small request, only on product pages, only when a size is stored.
       It carries type + tags, which is what participates() needs; it runs
       while we are already waiting on the theme's own scripts, so it costs
       no perceived time.

       NOW RETRIES (2026-08-11). This used the old `r.ok ? r.json() : null`
       pattern, so a 429 during the load burst failed silently and the stored
       size was simply never applied — the same defect that killed the
       collection labels. It uses fetchFeed() now.

       BUT A RETRY HERE IS NOT THE SAME AS ON A COLLECTION. The grid path only
       labels cards; this one MOVES THE SHOPPER'S SELECTION. The retry ladder
       runs to ~14.5s, and silently changing someone's size fifteen seconds into
       reading a product page is worse than never firing. So the apply is capped:
       past PDP_FEED_CUTOFF_MS we drop it. applySize's own "selection unchanged"
       check still guards the case where they picked a size while we waited. */
    var feedStartedAt = Date.now();
    fetchFeed('/products/' + encodeURIComponent(handle) + '.js',
      function (p) {
        if (!participates(p)) return;
        if (!window.customElements) return;
        if (Date.now() - feedStartedAt > PDP_FEED_CUTOFF_MS) return;
        var tag = host.tagName.toLowerCase();
        /* define() upgrades already-connected elements synchronously, so by
           the time this resolves connectedCallback has run and the change
           listener is attached. */
        window.customElements.whenDefined(tag).then(function () {
          /* ...but variant-radios being ready is NOT enough. Dawn's
             onVariantChange calls updatePickupAvailability() BEFORE
             updateVariantInput(), and that method only checks the
             <pickup-availability> element EXISTS, never that it has upgraded.
             Fire too early and it throws, the chosen variant never reaches the
             cart form, and this whole feature silently does nothing.

             The two are not ready at the same moment: variant-radios comes
             from global.min.js, which dzc-header.js promotes FIRST, while
             pickup-availability.js is promoted SECOND and is still in flight.
             Waiting only on variant-radios was a race, and it was being lost. */
          whenPickupReady(function () {
            whenSettled(function () { applySize(host, size, initialValue); });
          });
        });
      });
  }

  ready(function () {
    var cls = document.body.className || '';
    if (cls.indexOf('template-collection') !== -1) bridgeCollection();
    if (cls.indexOf('template-product') !== -1) bridgeProduct();
  });
})();
